import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography,
} from '@mui/material';
import { UploadFile as UploadIcon, DeleteOutline as RemoveIcon } from '@mui/icons-material';
import { useBranding, uploadLogo, removeLogo } from '../api/branding';

const LEVEL = { enterprise: 'enterprise', organization: 'organisation', region: 'region', clinic: 'clinic' };
const MAX_BYTES = 1024 * 1024;
// A header logo renders at 36px tall, so 1200px on the long side is far more than any
// screen needs — and usually lands well under the limit on the first try.
const MAX_SIDE = 1200;

const mb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Re-encode an image in the browser until it fits under `maxBytes`.
 *
 * WebP keeps a transparent background, which most logos have. Each pass that is still
 * too big scales the image down by a quarter; eight passes takes even a huge photo
 * under the limit. Browsers that can't encode WebP hand back a PNG instead, which the
 * server accepts too.
 */
async function shrinkImage(file, maxBytes = MAX_BYTES) {
  const bitmap = await createImageBitmap(file);
  let scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  for (let pass = 0; pass < 8; pass += 1) {
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
    if (blob && blob.size <= maxBytes) {
      const ext = blob.type === 'image/webp' ? 'webp' : 'png';
      return new File([blob], `logo.${ext}`, { type: blob.type });
    }
    scale *= 0.75;
  }
  throw new Error('too-large');
}

/**
 * Upload or remove the logo for the level this admin runs. Renders nothing for anyone
 * who can't change one, so Settings can include it unconditionally.
 */
export default function LogoSettings() {
  const { shown, own } = useBranding();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  // A file over the limit waits here while the admin decides: shrink it, or pick another.
  const [oversize, setOversize] = useState(null);
  if (!own) return null;

  const level = LEVEL[own.scope] || 'account';
  const mine = own.has_logo && shown?.scope === own.scope;
  const inherited = !own.has_logo && shown ? shown : null;

  const send = async (file, message = 'Logo updated') => {
    setBusy(true);
    try {
      await uploadLogo(file);
      toast.success(message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload the logo');
    } finally {
      setBusy(false);
    }
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) { setOversize(file); return; }
    send(file);
  };

  const shrinkAndSend = async () => {
    const file = oversize;
    setOversize(null);
    setBusy(true);
    let smaller;
    try {
      smaller = await shrinkImage(file);
    } catch {
      setBusy(false);
      toast.error('We could not shrink that image. Please choose a file under 1 MB.');
      return;
    }
    await send(smaller, `Logo reduced from ${mb(file.size)} to ${mb(smaller.size)} and updated`);
  };

  const clear = async () => {
    setBusy(true);
    try {
      await removeLogo();
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the logo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)', mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        Your logo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shown at the top of every screen for {own.name || `your ${level}`}
        {own.scope !== 'clinic' ? ` and everything beneath it that has no logo of its own` : ''}.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Box
          sx={{
            width: 180, height: 72, borderRadius: 2, flexShrink: 0,
            border: '1px dashed rgba(4, 47, 46, 0.25)', bgcolor: '#fff',
            display: 'grid', placeItems: 'center', p: 1.25,
          }}
        >
          {shown?.logo
            ? <Box component="img" src={shown.logo} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <Typography variant="caption" color="text.secondary">No logo yet</Typography>}
        </Box>
        <Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<UploadIcon />} disabled={busy}
              onClick={() => input.current?.click()}>
              {mine ? 'Replace' : 'Upload logo'}
            </Button>
            {mine && (
              <Button variant="text" size="small" color="inherit" startIcon={<RemoveIcon />} disabled={busy} onClick={clear}>
                Remove
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {inherited
              ? `Currently showing ${inherited.name}’s logo. Upload one to use your own. `
              : ''}
            PNG, JPEG or WebP, up to 1 MB — larger files can be reduced for you. A wide logo on a transparent background works best.
          </Typography>
        </Box>
      </Stack>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} />

      <Dialog open={Boolean(oversize)} onClose={() => setOversize(null)} maxWidth="xs" fullWidth>
        <DialogTitle>This logo is too large</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>{oversize?.name}</strong> is {oversize ? mb(oversize.size) : ''}. Logos must be
            under 1 MB.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We can reduce it for you automatically. It will look the same in the header, though
            very fine detail may soften slightly. For the sharpest result, upload a version
            under 1 MB yourself.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => { setOversize(null); input.current?.click(); }}>
            Choose another file
          </Button>
          <Button variant="contained" onClick={shrinkAndSend}>
            Reduce and upload
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
